import { computed, Injectable, linkedSignal, signal } from '@angular/core';
import { TableUIConfigType } from '../models/table-config.model';
import { TableUIResizeResultType } from '../models/table-resize.model';
import { MENU_COLUMN_WIDTH } from '../constants/menu-column.constants';

/**
 * Builds the `grid-template-columns` shared by the header and the rows. Every column keeps its own
 * width: one the user sized is `contentWidth * coefficient`, so a saved width comes back exactly;
 * any other starts at its header content floored at the config's `minWidth`, a default the user
 * may then drag below. The last visible non-menu column (the fill column) also stretches over
 * whatever the table has left, never below its default. A menu column is as wide as its widest
 * content, at least `MENU_COLUMN_WIDTH`, and the same in every row.
 */
@Injectable()
export class TableResizeUIService {
  private readonly coefficients = signal<ReadonlyMap<string, number>>(new Map());
  private readonly contentWidths = signal<ReadonlyMap<string, number>>(new Map());
  private readonly minWidths = signal<ReadonlyMap<string, number>>(new Map());
  private readonly resizableColumns = signal<ReadonlySet<string>>(new Set());
  /** Sized by the user in this table's lifetime, even if back to exactly its content width. */
  private readonly resizedColumns = signal<ReadonlySet<string>>(new Set());
  private readonly readyColumns = signal<ReadonlySet<string>>(new Set());
  private readonly menuColumns = signal<ReadonlySet<string>>(new Set());
  private readonly visibleColumns = signal<readonly string[]>([]);
  private readonly menuContentWidth = signal(0);

  private readonly _resizeEnd = signal<TableUIResizeResultType | null>(null);
  public readonly resizeEnd = this._resizeEnd.asReadonly();
  public readonly isTableResizing = signal(false);

  public readonly fillColumn = computed(
    () =>
      this.visibleColumns()
        .filter((column) => !this.menuColumns().has(column))
        .at(-1) ?? null,
  );

  /** Keeps the previous template while a newly shown column is still being measured. */
  public readonly gridTemplateColumns = linkedSignal<string | null, string>({
    source: () => this.buildTemplate(),
    computation: (template, previous) => template ?? previous?.value ?? '',
  });

  /**
   * Every config counts: the host passes the defaults first and the user's saved setup once it
   * loads. A config echoing this table's own `resizeEnd` carries the coefficients it already has.
   */
  public setConfig(config: TableUIConfigType): void {
    const columns = Object.entries(config);

    this.coefficients.update(
      (coefficients) =>
        new Map([
          ...coefficients,
          ...columns.map(([column, columnConfig]): [string, number] => [
            column,
            Math.max(columnConfig.customWithCoefficient ?? 1, 1),
          ]),
        ]),
    );
    this.minWidths.update(
      (minWidths) =>
        new Map([
          ...minWidths,
          ...columns.map(([column, columnConfig]): [string, number] => [
            column,
            columnConfig.minWidth ?? 0,
          ]),
        ]),
    );
  }

  public setColumnsOrder(columns: string[]): void {
    this.visibleColumns.set(columns);
  }

  public setMenuColumn(columnName: string, isMenu: boolean): void {
    if (this.menuColumns().has(columnName) === isMenu) {
      return;
    }

    this.menuColumns.update((columns) => {
      const next = new Set(columns);

      if (isMenu) {
        next.add(columnName);
      } else {
        next.delete(columnName);
      }

      return next;
    });
  }

  /**
   * The widest menu cell content seen so far. The column never narrows back, so it doesn't jump
   * while rows scroll in and out.
   */
  public fitMenuContent(width: number): void {
    const rounded = Math.ceil(width);

    if (rounded > this.menuContentWidth()) {
      this.menuContentWidth.set(rounded);
    }
  }

  public markColumnResizable(columnName: string | null): void {
    if (columnName !== null) {
      this.resizableColumns.update((columns) => new Set(columns).add(columnName));
    }
  }

  public markColumnAsReady(columnName: string | null): void {
    if (columnName !== null) {
      this.readyColumns.update((columns) => new Set(columns).add(columnName));
    }
  }

  public setInitialColumnWidth(columnName: string | null, width: number): void {
    if (columnName !== null) {
      this.contentWidths.update((widths) => new Map(widths).set(columnName, width));
    }
  }

  /**
   * A drag stores the width as a coefficient of the content width, never below 1; the fill column
   * never below its config default, the narrowest it is ever shown.
   */
  public updateColumnWidth(columnName: string, width: number): void {
    const contentWidth = this.contentWidths().get(columnName);

    if (!contentWidth || !this.coefficients().has(columnName)) {
      return;
    }

    const minWidth = columnName === this.fillColumn() ? (this.minWidths().get(columnName) ?? 0) : 0;

    this.coefficients.update((coefficients) =>
      new Map(coefficients).set(columnName, Math.max(width, minWidth, contentWidth) / contentWidth),
    );
    this.resizedColumns.update((columns) => new Set(columns).add(columnName));
  }

  /**
   * The width the column holds before the fill column stretches it; null until measured. The fill
   * column never goes below its config default, even when sized narrower: it only gives up free
   * space to the other columns down to that default.
   */
  public columnWidth(column: string): number | null {
    const contentWidth = this.contentWidths().get(column);
    const coefficient = this.coefficients().get(column);

    if (contentWidth === undefined || coefficient === undefined) {
      return null;
    }

    const defaultWidth = Math.max(contentWidth, this.minWidths().get(column) ?? 0);

    if (!this.isUserSized(column)) {
      return defaultWidth;
    }

    const width = contentWidth * coefficient;

    return column === this.fillColumn() ? Math.max(width, defaultWidth) : width;
  }

  public emitResizeEnd(): void {
    const result: TableUIResizeResultType = {};

    this.coefficients().forEach((coefficient, column) => {
      result[column] = { customWithCoefficient: coefficient };
    });

    this._resizeEnd.set(result);
  }

  private buildTemplate(): string | null {
    const columns = this.visibleColumns();
    const contentWidths = this.contentWidths();
    const coefficients = this.coefficients();
    const ready = this.readyColumns();

    const isEveryColumnReady = columns.every(
      (column) => ready.has(column) && contentWidths.has(column) && coefficients.has(column),
    );

    if (!columns.length || !isEveryColumnReady) {
      return null;
    }

    const fillColumn = this.fillColumn();

    return columns
      .map((column) => {
        if (this.menuColumns().has(column)) {
          return `${Math.max(MENU_COLUMN_WIDTH, this.menuContentWidth())}px`;
        }

        // Checked just above: every visible column has a content width and a coefficient.
        const width = this.columnWidth(column)!;

        return column === fillColumn ? `minmax(${width}px, 1fr)` : `${width}px`;
      })
      .join(' ');
  }

  /**
   * The server keeps no flag for "never sized", only the coefficient, so 1 reads as the default.
   * The one width this loses is a column dragged to exactly its content: it reloads at `minWidth`.
   */
  private isUserSized(column: string): boolean {
    return (
      this.resizableColumns().has(column) &&
      (this.resizedColumns().has(column) || (this.coefficients().get(column) ?? 1) > 1)
    );
  }
}
